import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';
import * as Sentry from '@sentry/react';
import {
  ensureFreshToken,
  forceRecovery,
  getKeycloak,
  initKeycloak,
  loginWithGoogle,
  loginWithPassword,
  logout as keycloakLogout,
} from './keycloak';
import { apiRequestJson, ApiRequestError } from '../api';
import { isTransientAuthError } from './authErrors';
import { enableSentryReplay, setSentryUser } from '../sentry';

export type CurrentUserProfile = {
  subject: string;
  email: string | null;
  displayName: string | null;
  admin: boolean;
  roles: string[];
  accessibleBrands: Array<{
    id: string;
    name: string;
    url: string;
    /** Authoritative backend decision for approval actions on this brand. */
    canApprove: boolean;
  }>;
  /**
   * The user's preferred IANA timezone (e.g. `'America/New_York'`).
   *
   * Returned by `GET /api/auth/me` (LUT-216). Null when the user hasn't set a
   * preference — UI defaults to UTC in that case. The backend may omit the
   * field entirely from the JSON response when null, so `refreshProfile`
   * normalises an absent field to `null`.
   */
  timezone: string | null;
  /**
   * Custom-terms login gate (set only for a designated signer who owes a
   * signature). When true, the app hard-blocks on the acceptance page until the
   * signer accepts; the three `requiredTerms*` fields name what to sign. The
   * backend omits the string fields when null, so they're normalised here.
   */
  termsAcceptanceRequired: boolean;
  requiredTermsBrandId: string | null;
  requiredTermsVariantKey: string | null;
  requiredTermsVersion: string | null;
  /**
   * Partner inline page-editor pilot flag — mirrors the backend
   * `partner.editor.enabled` config, surfaced on `GET /api/auth/me`
   * (`CurrentUserResponse#partnerEditorEnabled`). Gates the per-candidate
   * "Edit" action in the Approval Pipeline (spec §5.6). Optional on the wire
   * (the backend omits it when false / older responses predate it) and
   * normalised to `false` in {@link AuthProvider.refreshProfile}; default off
   * until the pilot is opened.
   */
  partnerEditorEnabled?: boolean;
  /**
   * Owner-only preview of the decision-first reporting canvas. The backend
   * derives this capability from the verified token identity; clients must
   * fail closed when an older response omits it.
   */
  reportingBetaEnabled?: boolean;
};

type AuthContextValue = {
  loading: boolean;
  authenticated: boolean;
  user: CurrentUserProfile | null;
  loginWithGoogle: (redirectPath?: string) => Promise<void>;
  loginWithPassword: (redirectPath?: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [user, setUser] = useState<CurrentUserProfile | null>(null);

  const refreshProfile = async () => {
    const token = await ensureFreshToken();
    if (!token) {
      setAuthenticated(false);
      setUser(null);
      return;
    }

    try {
      // Backend may omit `timezone` from the JSON when the user has no
      // preference set (NON_NULL serialization). Type the raw payload with
      // an optional timezone and normalise to `null` before storing in
      // state so consumers never have to disambiguate undefined vs null.
      const profile = await apiRequestJson<
        Omit<
          CurrentUserProfile,
          | 'timezone'
          | 'termsAcceptanceRequired'
          | 'requiredTermsBrandId'
          | 'requiredTermsVariantKey'
          | 'requiredTermsVersion'
        > & {
          timezone?: string | null;
          termsAcceptanceRequired?: boolean;
          requiredTermsBrandId?: string | null;
          requiredTermsVariantKey?: string | null;
          requiredTermsVersion?: string | null;
        }
      >('/api/auth/me');
      setAuthenticated(true);
      setUser({
        ...profile,
        timezone: profile.timezone ?? null,
        termsAcceptanceRequired: profile.termsAcceptanceRequired ?? false,
        requiredTermsBrandId: profile.requiredTermsBrandId ?? null,
        requiredTermsVariantKey: profile.requiredTermsVariantKey ?? null,
        requiredTermsVersion: profile.requiredTermsVersion ?? null,
        partnerEditorEnabled: profile.partnerEditorEnabled ?? false,
        reportingBetaEnabled: profile.reportingBetaEnabled ?? false,
      });
    } catch (err) {
      // Treat 429 (rate-limited) and transient network failures as recoverable:
      // keep the existing session, drop a breadcrumb, and schedule one
      // backoff retry so the UI self-heals instead of sitting indefinitely in
      // `authenticated=true, user=null`. Consumers already optional-chain on
      // `user`, so the interim render is harmless.
      if (isTransientAuthError(err)) {
        Sentry.addBreadcrumb({
          category: 'auth',
          level: 'warning',
          message: 'refreshProfile transient failure (retry scheduled)',
          data: {
            status: err instanceof ApiRequestError ? err.status : null,
          },
        });
        window.setTimeout(() => {
          void refreshProfile().catch(() => {});
        }, 2000);
        return;
      }
      throw err;
    }
  };

  useEffect(() => {
    let cancelled = false;

    const boot = async () => {
      try {
        const isAuthenticated = await initKeycloak();
        // Keycloak-js has now processed any OAuth callback fragment and
        // called history.replaceState to clean the URL. It is safe to
        // start Session Replay — its internal URL capture will no longer
        // see the OAuth code / state in window.location.
        enableSentryReplay();
        if (cancelled) return;
        setAuthenticated(isAuthenticated);
        if (isAuthenticated) {
          const keycloak = getKeycloak();
          setSentryUser(keycloak.tokenParsed?.sub);
          await refreshProfile();
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : '';
        if (message === 'keycloak-recovery-redirect-pending') {
          // keycloak.login() has issued a full-page navigation to Keycloak.
          // Keep the spinner on screen — we deliberately repurpose the
          // `cancelled` flag to skip the `finally` branch below so the
          // user never sees the unauthenticated TriggerLogin flash.
          cancelled = true;
          return;
        }
        if (isTransientAuthError(err)) {
          Sentry.addBreadcrumb({
            category: 'auth',
            level: 'warning',
            message: 'boot transient auth error (ignored)',
            data: { message },
          });
        } else {
          Sentry.captureException(err);
        }
        if (!cancelled) {
          setAuthenticated(false);
          setUser(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    boot();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const keycloak = getKeycloak();
    keycloak.onAuthSuccess = () => {
      setSentryUser(keycloak.tokenParsed?.sub);
      void refreshProfile();
    };
    keycloak.onAuthLogout = () => {
      setSentryUser(undefined);
      setAuthenticated(false);
      setUser(null);
    };
    keycloak.onTokenExpired = () => {
      void ensureFreshToken();
    };
    // Mid-session auth-flow errors (e.g. PKCE/state mismatch on a silent
    // token refresh) hit forceRecovery() rather than initKeycloak() — the
    // latter memoizes the already-successful init and would be a no-op
    // after a session is established. forceRecovery() resets the state
    // machine and hits attemptRecovery(), which navigates to a fresh
    // authorize URL. Swallow the rejection — recovery either navigates
    // away or trips the loop guard (surfaced via onAuthLogout).
    keycloak.onAuthError = (err) => {
      // keycloak-js sometimes invokes this with `undefined` when a fetch
      // rejects mid-callback (the real failure is already captured by
      // initKeycloak()'s catch). Drop a breadcrumb for those instead of
      // adding a second `Error: undefined` issue with no stacktrace.
      if (err) {
        Sentry.captureException(err, { tags: { source: 'keycloak.onAuthError' } });
      } else {
        Sentry.addBreadcrumb({
          category: 'auth',
          level: 'warning',
          message: 'keycloak.onAuthError (no payload)',
        });
      }
      void forceRecovery().catch(() => {});
    };
    keycloak.onAuthRefreshError = () => {
      Sentry.addBreadcrumb({ category: 'auth', message: 'onAuthRefreshError' });
      void forceRecovery().catch(() => {});
    };
    return () => {
      keycloak.onAuthSuccess = undefined;
      keycloak.onAuthLogout = undefined;
      keycloak.onTokenExpired = undefined;
      keycloak.onAuthError = undefined;
      keycloak.onAuthRefreshError = undefined;
    };
  }, []);

  const value = useMemo<AuthContextValue>(() => ({
    loading,
    authenticated,
    user,
    loginWithGoogle,
    loginWithPassword,
    logout: async () => {
      setSentryUser(undefined);
      await keycloakLogout();
    },
    refreshProfile,
    getAccessToken: ensureFreshToken,
  }), [loading, authenticated, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

/**
 * Non-throwing variant of {@link useAuth} for components that must render
 * outside an {@link AuthProvider} (e.g. isolated component tests that don't
 * stand up the full provider stack). Returns `null` when no provider is
 * mounted; callers treat that as "identity unknown" and degrade gracefully
 * (the server remains the true authority regardless of what the UI shows).
 */
export const useAuthOptional = (): AuthContextValue | null =>
  useContext(AuthContext);
